import { useEffect, useState } from "react";
import { Tag, Maximize2, Briefcase, Clock } from "lucide-react";
import { TbRulerMeasure, TbBuildingSkyscraper } from "react-icons/tb";

import {
  DetailItem,
  DetailRow,
  DeveloperInfo,
  ImageGallery,
  MapEmbed,
} from "../../../../components/admin/common/details-components";
import {
  formatPrettyAddress,
  formatPrice,
  formatFloor,
} from "../../../../utils/helpers";
import PropertyTitleCard from "./PropertyTitleCard";

const formatTransactionType = (type) => {
  const typeMap = {
    sale: "For Sale",
    resale: "For Resale",
    lease: "For Lease",
    rent: "For Rent",
  };
  return typeMap[type] || type || "Not Specified";
};

const PropertyDetailsCard = ({ property }) => {
  const propertyData = property?.property;
  const propertyAge = propertyData?.propertyAge;
  const suitableFor = propertyData?.suitableFor;
  const constructionDetails = propertyData?.allowedForConstructionDetails;

  return (
    <div className="w-full bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
      {/* Transaction Type */}
      <div className="flex items-center gap-x-4 text-gray-900 font-semibold text-lg sm:text-xl mb-3">
        <div className="flex items-center gap-1">
          <Tag className="mr-2 flex-shrink-0 w-4 h-4 scale-[-1]" />
          {formatPrice(constructionDetails.expectedPrice)}
        </div>
      </div>

      {/* Property Type & Age */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-3 border-y border-gray-200">
        <DetailItem
          icon={<Briefcase className="text-gray-400 w-5 h-5" />}
          label="Suitable For"
          value={suitableFor}
          borderRight
        />
        {propertyAge && (
          <DetailItem
            icon={<Clock className="text-gray-400 w-5 h-5" />}
            label="Property Age"
            value={`${propertyAge} years`}
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-3 border-b border-gray-200">
        <div className="sm:col-span-1">
          {constructionDetails?.carpetArea?.value && (
            <DetailItem
              icon={<TbRulerMeasure className="text-gray-400 w-5 h-5" />}
              label="Carpet Area"
              value={`${constructionDetails?.carpetArea.value} ${
                constructionDetails?.carpetArea.unit || "sq. ft."
              }`}
              borderRight
            />
          )}
        </div>
        <div className="sm:col-span-1">
          {constructionDetails?.superArea?.value && (
            <DetailItem
              icon={<Maximize2 className="text-gray-400 w-5 h-5" />}
              label="Super Area"
              value={`${constructionDetails?.superArea.value} ${
                constructionDetails?.superArea.unit || "sq. ft."
              }`}
              borderRight
            />
          )}
        </div>
        <div className="sm:col-span-1">
          {constructionDetails?.floor && (
            <DetailItem
              icon={<TbBuildingSkyscraper className="text-gray-400 w-5 h-5" />}
              label="Floor"
              value={`${formatFloor(constructionDetails?.floor)}`}
            />
          )}
        </div>
      </div>

      {/* Construction Details */}
      <div className="pt-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {constructionDetails.pricePerSqft && (
            <DetailItem
              icon={<Tag className="text-gray-400 w-5 h-5" />}
              label="Price per sqft"
              value={formatPrice(constructionDetails.pricePerSqft)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const MoreDetailsCard = ({ property }) => {
  const propertyData = property?.property;
  const constructionDetails = propertyData?.allowedForConstructionDetails;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 w-full">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
        More Details
      </h3>

      <div className="space-y-3">
        <DetailRow
          label="Transaction Type"
          value={formatTransactionType(propertyData?.transactionType)}
        />
        <DetailRow label="Suitable For" value={propertyData?.suitableFor} />
        {propertyData?.propertyAge && (
          <DetailRow
            label="Property Age"
            value={`${propertyData.propertyAge} years`}
          />
        )}

        {constructionDetails && (
          <>
            {constructionDetails.availability && (
              <DetailRow
                label="Availability"
                value={
                  constructionDetails.availability === "under_construction"
                    ? "Under Construction"
                    : "Ready to Move"
                }
              />
            )}
            {constructionDetails.expectedPrice && (
              <DetailRow
                label="Expected Price"
                value={formatPrice(constructionDetails.expectedPrice)}
              />
            )}
            {constructionDetails.pricePerSqft && (
              <DetailRow
                label="Price per sqft"
                value={formatPrice(constructionDetails.pricePerSqft)}
              />
            )}
          </>
        )}
        {property?.location && (
          <DetailRow
            label="Address"
            value={property?.location?.address?.formatted}
          />
        )}
      </div>
    </div>
  );
};

const AboutProperty = ({ property }) => {
  const description = property?.description;

  if (!description) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6 mx-auto w-full border">
      <h2 className="font-medium text-gray-900 tracking-[0.01em] text-xl sm:text-2xl mb-3">
        About Property
      </h2>

      <div className="border-t-2 border-gray-200 mb-4"></div>

      <div className="text-sm sm:text-base md:text-[1rem] lg:text-[1.1rem] tracking-wide text-gray-600 md:text-justify w-full">
        <p className="text-[16px] text-gray-600 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

const AdminCommercialPropertyDetails = (prop) => {
  const [property, setProperty] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setProperty(prop?.property);
    console.log("PRoperty:", prop);
  }, [prop]);

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading property details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <main className="max-w-full mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <ImageGallery images={property.images} title={property.title} />
            <AboutProperty property={property} />
            <DeveloperInfo property={property} />
            <MapEmbed
              className="h-[20rem]"
              coordinates={property?.location?.coordinates}
              address={formatPrettyAddress(property?.location)}
            />
          </div>

          <div className="space-y-4">
            <PropertyTitleCard property={property} />
            <PropertyDetailsCard property={property} />
            <MoreDetailsCard property={property} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCommercialPropertyDetails;
